import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  TripLegStatus,
  TripLegStatusBC,
} from '../../data/models/trip-leg-status.model';
import { useState } from 'react';
import { TripLegResponseItem } from '../../data/models/trip-leg.model';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import {
  overrideTripLegStatus,
  overrideTripLegStatusCanceledLateToCompleted,
  overrideTripLegStatusCanceledLateToNoLoad,
  overrideTripLegStatusNoLoadToCompleted,
  overrideTripLegStatusOnsiteToNoLoad,
} from '../../data/trip.slice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
type StatusDialogProps = {
  selectedTripLeg: TripLegResponseItem | null;
  open: boolean;
  onClose: () => void;
  providerId: number;
  targetStatus: TripLegStatus;
  originalStatus: TripLegStatusBC | null;
  onStatusChanged?: (
    tripLegReferenceID: string,
    newStatus: TripLegStatusBC,
    timestamps: {
      onSite?: string;
      onBoard?: string;
      completed?: string;
      noLoad?: string;
      canceledLate?: string;
    },
  ) => void;
};

const buildLuxonDate = (
  trip: TripLegResponseItem | null,
  time: string,
): DateTime => {
  const timezone = trip?.pickUpAddressTimeZoneIANA;
  const date = trip?.appointmentDate;
  return DateTime.fromFormat(`${date} ${time}`, 'yyyy-MM-dd h:mm a', {
    zone: timezone,
  }).toUTC();
};

const LabeledTimeField = ({
  label,
  onChange,
}: {
  label: string;
  onChange: (value: string) => void;
}) => {
  return (
    <>
      <Typography sx={{ mt: 1, mb: 1 }}>{label}</Typography>
      <TimeField
        defaultValue={null}
        onChange={(value: any) => {
          if (value) {
            const time = value.toLocaleString(DateTime.TIME_SIMPLE);
            onChange(time);
          }
        }}
      />
    </>
  );
};

const StatusDialog = ({
  selectedTripLeg,
  open,
  onClose,
  providerId,
  targetStatus,
  originalStatus,
  onStatusChanged,
}: StatusDialogProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [timeFormValue, setTimeFormValue] = useState<string | null>(null);
  const [onSiteTimeFormValue, setOnSiteTimeFormValue] = useState<string | null>(
    null,
  );
  const [onBoardTimeFormValue, setOnBoardTimeFormValue] = useState<
    string | null
  >(null);
  const [completedTimeFormValue, setCompletedTimeFormValue] = useState<
    string | null
  >(null);

  const renderAcceptedToNoLoadFields = () => (
    <>
      <LabeledTimeField
        label={t('tripLegStatus.onsiteTime')}
        onChange={(time) =>
          setOnSiteTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
      <LabeledTimeField
        label={t('tripLegStatus.noLoadTime')}
        onChange={(time) =>
          setTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
    </>
  );

  const renderCanceledLateToCompletedFields = () => (
    <>
      <LabeledTimeField
        label={t('tripLegStatus.onsiteTime')}
        onChange={(time) =>
          setOnSiteTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
      <LabeledTimeField
        label={t('tripLegStatus.onBoardTime')}
        onChange={(time) =>
          setOnBoardTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
      <LabeledTimeField
        label={t('tripLegStatus.completedTime')}
        onChange={(time) =>
          setCompletedTimeFormValue(
            buildLuxonDate(selectedTripLeg, time).toISO(),
          )
        }
      />
    </>
  );

  const renderNoLoadToCompletedFields = () => (
    <>
      <LabeledTimeField
        label={t('tripLegStatus.onBoardTime')}
        onChange={(time) =>
          setOnBoardTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
      <LabeledTimeField
        label={t('tripLegStatus.completedTime')}
        onChange={(time) =>
          setCompletedTimeFormValue(
            buildLuxonDate(selectedTripLeg, time).toISO(),
          )
        }
      />
    </>
  );

  const renderCanceledLateToNoLoadFields = () => (
    <>
      <LabeledTimeField
        label={t('tripLegStatus.onsiteTime')}
        onChange={(time) =>
          setOnSiteTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
      <LabeledTimeField
        label={t('tripLegStatus.noLoadTime')}
        onChange={(time) =>
          setTimeFormValue(buildLuxonDate(selectedTripLeg, time).toISO())
        }
      />
    </>
  );

  const setTripLegStatus = (
    passengerUUID: string,
    tripLegReferenceID: string,
    tripLegStatusDateTimeUTC: string,
    status: TripLegStatus,
  ) => {
    dispatch(
      overrideTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        status,
        tripLegStatusDateTimeUTC,
        providerId,
        status === TripLegStatus.NoLoad
          ? ['NOPASSENGERFORDRIVER']
          : ['LATEDOCRESCHEDULE'],
      ),
    );
  };

  const setTripLegStatusOnsiteToNoLoad = (
    passengerUUID: string,
    tripLegReferenceID: string,
    onSiteDateTimeUTC: string,
    noLoadDateTimeUTC: string,
  ) => {
    dispatch(
      overrideTripLegStatusOnsiteToNoLoad(
        passengerUUID,
        tripLegReferenceID,
        onSiteDateTimeUTC,
        noLoadDateTimeUTC,
        providerId,
      ),
    );
  };

  const setTripLegStatusCanceledLateToNoLoad = (
    passengerUUID: string,
    tripLegReferenceID: string,
    onSiteDateTimeUTC: string,
    noLoadDateTimeUTC: string,
    status: TripLegStatus,
  ) => {
    dispatch(
      overrideTripLegStatusCanceledLateToNoLoad(
        passengerUUID,
        tripLegReferenceID,
        status,
        onSiteDateTimeUTC,
        noLoadDateTimeUTC,
        providerId,
      ),
    );
  };

  const setTripLegStatusNoLoadToCompleted = (
    passengerUUID: string,
    tripLegReferenceID: string,
    onBoardDateTimeUTC: string,
    completedDateTimeUTC: string,
    status: TripLegStatus,
  ) => {
    dispatch(
      overrideTripLegStatusNoLoadToCompleted(
        passengerUUID,
        tripLegReferenceID,
        onBoardDateTimeUTC,
        completedDateTimeUTC,
        providerId,
      ),
    );
  };

  const setTripLegStatusCanceledLateToCompleted = (
    passengerUUID: string,
    tripLegReferenceID: string,
    onSiteDateTimeUTC: string,
    onBoardDateTimeUTC: string,
    completedDateTimeUTC: string,
    status: TripLegStatus,
  ) => {
    dispatch(
      overrideTripLegStatusCanceledLateToCompleted(
        passengerUUID,
        tripLegReferenceID,
        onSiteDateTimeUTC,
        onBoardDateTimeUTC,
        completedDateTimeUTC,
        providerId,
      ),
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const passengerUUID = selectedTripLeg?.passengerUUID ?? '';
    const tripLegReferenceID = selectedTripLeg?.tripLegReferenceID ?? '';

    const timestamps: {
      onSite?: string;
      onBoard?: string;
      completed?: string;
      noLoad?: string;
      canceledLate?: string;
    } = {};

    if (
      originalStatus === TripLegStatusBC.CanceledLate &&
      targetStatus === TripLegStatus.NoLoad
    ) {
      timestamps.onSite = onSiteTimeFormValue ?? '';
      timestamps.noLoad = timeFormValue ?? '';
      setTripLegStatusCanceledLateToNoLoad(
        passengerUUID,
        tripLegReferenceID,
        onSiteTimeFormValue ?? '',
        timeFormValue ?? '',
        TripLegStatus.Onsite,
      );
    } else if (
      originalStatus === TripLegStatusBC.Accepted &&
      targetStatus === TripLegStatus.NoLoad
    ) {
      timestamps.onSite = onSiteTimeFormValue ?? '';
      timestamps.noLoad = timeFormValue ?? '';
      setTripLegStatusOnsiteToNoLoad(
        passengerUUID,
        tripLegReferenceID,
        onSiteTimeFormValue ?? '',
        timeFormValue ?? '',
      );
    } else if (
      originalStatus === TripLegStatusBC.NoLoad &&
      targetStatus === TripLegStatus.Completed
    ) {
      timestamps.onBoard = onBoardTimeFormValue ?? '';
      timestamps.completed = completedTimeFormValue ?? '';
      setTripLegStatusNoLoadToCompleted(
        passengerUUID,
        tripLegReferenceID,
        onBoardTimeFormValue ?? '',
        completedTimeFormValue ?? '',
        TripLegStatus.Completed,
      );
    } else if (
      originalStatus === TripLegStatusBC.CanceledLate &&
      targetStatus === TripLegStatus.Completed
    ) {
      timestamps.onSite = onSiteTimeFormValue ?? '';
      timestamps.onBoard = onBoardTimeFormValue ?? '';
      timestamps.completed = completedTimeFormValue ?? '';
      setTripLegStatusCanceledLateToCompleted(
        passengerUUID,
        tripLegReferenceID,
        onSiteTimeFormValue ?? '',
        onBoardTimeFormValue ?? '',
        completedTimeFormValue ?? '',
        TripLegStatus.Completed,
      );
    } else {
      if (targetStatus === TripLegStatus.NoLoad) {
        timestamps.noLoad = timeFormValue ?? '';
      } else if (targetStatus === TripLegStatus.CanceledLate) {
        timestamps.canceledLate = timeFormValue ?? '';
      }
      setTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        timeFormValue ?? '',
        targetStatus,
      );
    }

    // Call the callback with optimistic update data
    if (onStatusChanged) {
      const newStatusBC =
        targetStatus === TripLegStatus.NoLoad
          ? TripLegStatusBC.NoLoad
          : targetStatus === TripLegStatus.CanceledLate
          ? TripLegStatusBC.CanceledLate
          : TripLegStatusBC.Completed;
      onStatusChanged(tripLegReferenceID, newStatusBC, timestamps);
    }

    resetFormState();
    onClose();
  };

  const resetFormState = () => {
    setOnSiteTimeFormValue(null);
    setTimeFormValue(null);
    setOnBoardTimeFormValue(null);
    setCompletedTimeFormValue(null);
  };
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t('tripLegStatus.dialogTitle', {
          tripStatus:
            targetStatus === TripLegStatus.NoLoad
              ? t('tripLegStatus.noLoad')
              : targetStatus === TripLegStatus.CanceledLate
              ? t('tripLegStatus.canceledLate')
              : t('tripLegStatus.completed'),
          tripLegReferenceID: selectedTripLeg?.tripLegReferenceID,
        })}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <form onSubmit={handleSubmit}>
            {originalStatus === TripLegStatusBC.Accepted &&
              targetStatus === TripLegStatus.NoLoad &&
              renderAcceptedToNoLoadFields()}
            {originalStatus === TripLegStatusBC.CanceledLate &&
              targetStatus === TripLegStatus.Completed &&
              renderCanceledLateToCompletedFields()}
            {originalStatus === TripLegStatusBC.NoLoad &&
              targetStatus === TripLegStatus.Completed &&
              renderNoLoadToCompletedFields()}
            {originalStatus === TripLegStatusBC.CanceledLate &&
              targetStatus === TripLegStatus.NoLoad &&
              renderCanceledLateToNoLoadFields()}
            {(targetStatus === TripLegStatus.NoLoad ||
              targetStatus === TripLegStatus.CanceledLate) &&
              !(
                originalStatus === TripLegStatusBC.CanceledLate &&
                targetStatus === TripLegStatus.NoLoad
              ) &&
              !(
                originalStatus === TripLegStatusBC.Accepted &&
                targetStatus === TripLegStatus.NoLoad
              ) && (
                <>
                  <TimeField
                    defaultValue={null}
                    onChange={(value) => {
                      if (value) {
                        const timezone =
                          selectedTripLeg?.pickUpAddressTimeZoneIANA;
                        const date = selectedTripLeg?.appointmentDate;
                        const time = DateTime.fromISO(value).toLocaleString(
                          DateTime.TIME_SIMPLE,
                        );
                        const newDateTime = DateTime.fromFormat(
                          `${date} ${time}`,
                          'yyyy-MM-dd h:mm a',
                          { zone: timezone },
                        ).toUTC();
                        setTimeFormValue(newDateTime.toISO());
                      }
                    }}
                  />
                </>
              )}
            <DialogActions>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}>
                <Button
                  onClick={() => {
                    onClose();
                    setTimeFormValue(null);
                    resetFormState();
                  }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {t('common.save')}
                </Button>
              </Box>
            </DialogActions>
          </form>
        </LocalizationProvider>
      </DialogContent>
    </Dialog>
  );
};

export { StatusDialog };
